// Single source of truth for the user-facing algorithm metadata
// (titles, tooltips, "what it does" copy). Centralised so both the
// individual analyser panels and the meta-endpoint summary tile
// reference the same description.

import React from 'react';
import HealingIcon from '@mui/icons-material/Healing';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BoltIcon from '@mui/icons-material/Bolt';
import RouterIcon from '@mui/icons-material/Router';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

export const ALGO_HEALTH = 'health';
export const ALGO_TOPOLOGY = 'topology';
export const ALGO_POWER = 'power';
export const ALGO_CHANNEL = 'channel';
export const ALGO_BACKUP = 'backup';
export const ALGO_OPTIMIZE = 'optimize';

// Shared bullet-list style for description JSX blocks.
const UL = ({ children }) => (
  <ul style={{ margin: '4px 0 4px', paddingLeft: 18 }}>{children}</ul>
);

export const algorithmCatalog = {
  [ALGO_OPTIMIZE]: {
    id: ALGO_OPTIMIZE,
    title: 'Optimize (Meta)',
    short: 'Run the full optimisation pipeline',
    icon: AutoFixHighIcon,
    color: 'primary',
    isMeta: true,
    tooltip:
      'Chains Health → Topology → TX Power → Channels → Backup as one plan (block-coordinate descent). Each step feeds the next. rf_telemetry enhances every stage when available. When a site-wide iperf run with any topology overlap exists, measured throughput anchors decisions for the matching edges.',
    description: (
      <>
        Chains all analysers into a single ordered plan. Each step feeds its changes into the next.
        <UL>
          <li><strong>Health</strong> (once) — triage snapshot, builds a focus list</li>
          <li><strong>Topology</strong> (each iteration) — re-parent to raise weak-path throughput</li>
          <li><strong>TX Power</strong> (each iteration) — drop overpowered radios</li>
          <li><strong>Channels</strong> (each iteration) — DSATUR re-colouring</li>
          <li><strong>P10 Guard</strong> — prunes topology actions if the combined plan would degrade the 10th-percentile path throughput below baseline</li>
          <li><strong>Backup</strong> (once) — failover advisory on the final tree</li>
        </UL>
        When devices report <em>rf_telemetry</em>, each stage uses richer signals (own vs foreign
        airtime, FCS errors, recent loss/retry deltas, phy_rate_mbps). Older firmware falls back
        gracefully.
        <br /><br />
        <strong>Iperf-anchored mode</strong> (default ON): when a previously-run site-wide iperf
        session has any overlap with the current tree topology, measured throughput overrides
        theoretical estimates for the matching edges (unmeasured edges fall back gracefully):
        <UL>
          <li>
            <strong>Topology</strong> penalises candidate parents whose measured path is a bottleneck,
            caps proposed kbps at the measured ceiling, and surfaces{' '}
            <em>measured_bottleneck</em> issues for low-throughput edges.
          </li>
          <li>
            <strong>Channel</strong> boosts severity (&times;1.5) for conflicts on degraded iperf
            paths, demotes (&times;0.5) for confirmed-healthy paths.
          </li>
          <li>
            <strong>TX Power</strong> suppresses power-down when iperf proves the link healthy (&gt;200 Mbps,
            &lt;1% loss), and flags <em>interference_suspected</em> when RSSI is strong but measured
            throughput is low.
          </li>
          <li>
            <strong>Score baseline</strong> anchors the &ldquo;before&rdquo; reading of p10
            path-throughput on the iperf-measured value instead of a model estimate.
          </li>
        </UL>
        No iperf run with overlap? The pipeline runs unmodified with theoretical estimates and a warning banner.
      </>
    ),
    algorithm: 'Block-coordinate descent — Health (once) → loop[Topology → TX Power → Channels] → Backup (once). Optionally iperf-anchored: measured throughput overrides theoretical estimates per edge/path when any site-wide iperf run with topology overlap is selected.',
    measures:
      'Composite of Topology (50%, Δ p10 path-throughput-kbps), Channel (30%, conflict reduction), Power (20%, Σ mW saved) improvements. When iperf-anchored, the "before" p10 reading uses measured throughput.',
    actsOn: 'Re-parent, set AP TX-power (downlink), set STA TX-power (uplink), set channel — operator-driven.',
  },

  [ALGO_HEALTH]: {
    id: ALGO_HEALTH,
    title: 'Health (Triage)',
    short: 'Single-snapshot health smell-test',
    icon: HealingIcon,
    color: 'primary',
    isMeta: false,
    advisory: true,
    tooltip:
      'Single-snapshot triage: latency, jitter, TX-failures, weak uplinks, long paths, offline. With rf_telemetry: also flags airtime saturation, external interference, RF errors, link retry bursts. Advisory only — no changes applied.',
    description: (
      <>
        Reads one snapshot of every device and flags issues by severity.
        <br /><br />
        <strong>Classic checks:</strong> latency, jitter, TX-failure %, uplink RSSI, hop depth, offline.
        <br /><br />
        <strong>With rf_telemetry — four additional issue types:</strong>
        <UL>
          <li><strong>airtime_saturation</strong> — channel busy ≥ 60%</li>
          <li><strong>external_interference</strong> — foreign airtime ≥ 25%</li>
          <li><strong>rf_errors</strong> — FCS or ACK failure rate ≥ 5%</li>
          <li><strong>link_retries_burst</strong> — per-station retry rate ≥ 20%</li>
        </UL>
        Each issue hints which analyser to run next. Devices without rf_telemetry get the classic
        checks only.
        <br /><br />
        <strong>Cross-run advisories (from the persistent RRM state):</strong>
        <UL>
          <li><strong>device_not_responding</strong> — three or more consecutive plans
            haven&apos;t landed on this radio. The optimizer keeps re-trying; investigate the
            device&apos;s connectivity / firmware status.</li>
          <li><strong>channel_oscillation</strong> — recent plans show A→B→A→B
            channel ping-pong on this radio. The optimizer applies a stricter improvement
            floor to break the flap.</li>
        </UL>
      </>
    ),
    algorithm: 'Single-snapshot weighted heuristic (latency, jitter, TX-failure %, uplink RSSI, hop asymmetry) + rf_telemetry: airtime saturation, foreign interference, FCS/ACK errors, retry bursts',
    measures: 'Health score 0–100. 100 = clean. <40 = run the optimiser.',
    actsOn: 'Nothing — advisory only. Treat issues as triage hints.',
  },

  [ALGO_TOPOLOGY]: {
    id: ALGO_TOPOLOGY,
    title: 'Topology (Re-parenting)',
    short: 'Suggest better parents per Node',
    icon: AccountTreeIcon,
    color: 'primary',
    isMeta: false,
    tooltip:
      'Local search over the parent graph. Cost = child STA uplink profile (MCS/NSS/BW → kbps), falling back to parent AP measurement, Ack_Signal_Avg, scan RSSI. With rf_telemetry: prefers phy_rate_mbps, uses per-window deltas, marks idle stations as stale.',
    description: (
      <>
        Proposes one re-parent per Node to raise weak-path throughput p10.
        <br /><br />
        <strong>Cost ladder</strong> (best signal wins):
        <UL>
          <li>Child STA uplink profile — MCS / NSS / BW → estimated kbps</li>
          <li>Parent AP delivered kbps + loss% + retry%</li>
          <li>Ack_Signal_Avg</li>
          <li>Scan RSSI</li>
        </UL>
        <strong>With rf_telemetry:</strong>
        <UL>
          <li>Prefers <strong>phy_rate_mbps</strong> over tx_rate.bitrate</li>
          <li>Uses recent per-window loss/retry deltas — not cumulative ratios</li>
          <li>Marks idle stations (inactive_ms &gt; 600 s) as stale; suppresses their loss/retry penalty</li>
          <li>Nodes with <em>link_retries_burst</em> get a 30% lower improvement threshold</li>
        </UL>
        Moves are capped by blast radius. Already-moved nodes are pinned for the rest of the plan.
      </>
    ),
    algorithm: 'Tabu local search from the current tree; objective = Δ weak-path p10 (kbps); EffectiveLinkCostV2 (phy_rate_mbps + recent deltas + staleness gating); blast-radius capped',
    measures:
      'Weak-path throughput p10 (kbps, higher is better) — the 10th percentile across node-to-CAP paths. Summed effective cost is the secondary metric.',
    actsOn: 'POST /tree-mgmt/{serial}/connect-to-ap — sets parent.',
  },

  [ALGO_POWER]: {
    id: ALGO_POWER,
    title: 'TX Power',
    short: 'Symmetric audit — AP downlink, STA uplink, plus self-interference detection',
    icon: BoltIcon,
    color: 'primary',
    isMeta: false,
    tooltip:
      'Two-arm audit (downlink AP TX, uplink STA TX) plus a self-interference audit that detects relays whose AP desensitises its co-located STA receiver. Defaults: uplink/downlink severity weights are symmetric (1.0). Coupling reconciles to the binding-side maximum (never drops a coupled radio below its per-link minimum). EIRP regulatory cap available (US/EU/IL) — when set, CAP 11 dBi / Node 0 dBi antenna gains clamp the effective radio cap. With rf_telemetry: vetoes drops when ack_failure_rate ≥ 5% or tx_airtime_pct ≥ 40% (30-min hysteresis on the next plan). Min TX floor 6 dBm; double-drop guard ≤ 9 dB per radio.',
    description: (
      <>
        Three audit passes:
        <UL>
          <li>
            <strong>Downlink</strong> — per AP radio, find the minimum TX that keeps the weakest
            child healthy. Uses child neighbour-scan RSSI or parent ack_signal_avg.
          </li>
          <li>
            <strong>Uplink</strong> — per child STA radio, check if TX exceeds what path loss
            warrants. Severity weight defaults to <strong>1.0 (symmetric)</strong>;
            operators can opt back into the legacy 1.5× uplink bias via the optimize form.
          </li>
          <li>
            <strong>Self-interference</strong> — for every relay (uplink STA + downlink AP),
            combine structural coupling (same physical radio &gt; same band &gt; adjacent bands)
            with telemetry symptoms (LossPct / RetryPct / ack_failure_rate) and iperf E2E-vs-hop
            confirmation. When evidence converges, emit a coordinated AP+STA power-down bundle
            with 0 dB headroom (vs the standard 2 dB) — or upgrade an existing min-power
            recommendation to the stronger one. Reason is marked
            <em> "self-interference suspected: ..."</em>.
          </li>
        </UL>
        <strong>With rf_telemetry — TX-drop is vetoed when:</strong>
        <UL>
          <li>ack_failure_rate ≥ 5% — link is already struggling</li>
          <li>tx_airtime_pct ≥ 40% — radio is heavily loaded</li>
        </UL>
        Vetoed actions still appear as issues with a <em>SuppressedReason</em>. A radio vetoed in
        the last 30 min gets a stricter improvement floor on the next plan (hysteresis) so single
        noisy polls don&apos;t flap the recommendation.
        <br /><br />
        <strong>EIRP regulatory cap (US/EU/IL):</strong> when enabled in the optimize form, the
        per-radio TX is clamped at <em>min(hardware 18 dBm, EIRP_cap − antenna_gain)</em>. CAP
        directional antenna defaults to <strong>11 dBi</strong>, Node omni defaults to{' '}
        <strong>0 dBi</strong>. Result: under EU/IL (23 dBm EIRP), CAPs cap at 12 dBm; Nodes
        stay at 18 dBm.
        <br /><br />
        <strong>Intra-device coupling:</strong> when two radios on the same chassis (CAP dual-AP
        / multi-radio Node) diverge by more than 2 dB, the plan reconciles to the{' '}
        <strong>binding-side maximum</strong> (the link-safe ceiling), not the arithmetic mean —
        the old "mean" rule could quietly drop a downlink arm below its per-link minimum.
        <br /><br />
        <strong>Iperf gate</strong>: when iperf measured the edge healthy (≥ 200 Mbps, &lt; 1%
        loss), drops are suppressed. Coverage-floor RAISE actions are <em>never</em> suppressed
        because a link below the floor isn&apos;t healthy regardless of what a transient burst
        showed.
        <br /><br />
        Min TX floor: 6 dBm. Double-drop guard: no radio loses more than ~9 dB across the entire
        plan.
      </>
    ),
    algorithm:
      'Two-pass minimum-power heuristic: (1) downlink — AP TX vs worst-child RSSI + margin; (2) uplink — STA TX vs parent ack_signal_avg + margin, symmetric severity (1.0×, tunable); EIRP cap = min(hw18 dBm, EIRP_cap − antenna_gain); intra-device coupling = binding-side max; rf_telemetry gates: ack_failure ≥ 5% or tx_airtime ≥ 40% with 30-min hysteresis; double-drop guard ≤ 9 dB',
    measures:
      'Σ TX mW across AP (downlink) + STA (uplink) radios — lower is better. Summary breaks out mW saved per direction.',
    actsOn:
      'Downlink: PATCH /devices/{serial}/config — sets tx_power (AP interface). Uplink: sets tx_power_up (STA interface). The two arms never double-count the same chip.',
  },

  [ALGO_CHANNEL]: {
    id: ALGO_CHANNEL,
    title: 'Channels (DSATUR)',
    short: 'Resolve co-channel interference',
    icon: RouterIcon,
    color: 'primary',
    isMeta: false,
    tooltip:
      'DSATUR colouring on GPS + scan-triaged interference graph. Severity uses real spectral overlap (HT80 ch36 vs ch48 = same block, full overlap). With rf_telemetry: foreign airtime drives conflict weight; FCS-error penalty on candidate channels. Hard reject on same-block critical neighbours.',
    description: (
      <>
        Three-stage process:
        <UL>
          <li>Build interference graph from GPS distance + spectral overlap.</li>
          <li>
            Filter with neighbour-scan reports — drop GPS-close but RF-isolated pairs; fall back to
            GPS-only if scan data is missing.
          </li>
          <li>Assign channels with DSATUR colouring (honours bandwidth + regulatory domain).</li>
        </UL>
        <strong>Bandwidth-honest conflict severity:</strong>
        <UL>
          <li>
            Two APs are scored by the actual <strong>fraction of physical spectrum they share</strong>,
            not their primary-channel distance. Two HT80 APs in the same bonded block (e.g. ch 36
            and ch 48 both occupy 5170-5250 MHz) score 1.0 — moving one primary doesn&apos;t
            actually reduce overlap.
          </li>
          <li>
            Mixed bandwidths use the narrower side&apos;s perspective: an HT20 AP sitting inside an
            HT80 neighbour&apos;s block scores 1.0 (the HT20 is fully blocked).
          </li>
          <li>
            <strong>Hard same-block reject:</strong> the picker never proposes a channel that stays
            in the same bonded block as a critical-severity neighbour. Forces a real move (different
            block) or triggers the bandwidth downgrade pass.
          </li>
        </UL>
        <strong>Bandwidth awareness:</strong>
        <UL>
          <li>
            Candidate channels are filtered by max supported bandwidth — e.g. ch 165 (20 MHz only)
            is excluded when the radio runs at HT80.
          </li>
          <li>
            When high-severity conflicts persist, the planner tries{' '}
            <strong>stepwise bandwidth downgrades</strong> — first HT80 → HT40, then HT40 → HT20 if
            40 MHz didn&apos;t resolve the conflict. A scaled penalty (larger for bigger drops)
            ensures same-BW moves are always preferred when comparable.
          </li>
        </UL>
        <strong>With rf_telemetry:</strong>
        <UL>
          <li>
            Conflict severity uses <strong>ForeignAirtimeFraction</strong> (= channel_busy − own
            TX/RX) — separates "my children are loading this channel" from "an external AP is
            hammering it". Each side falls back independently for mixed fleets.
          </li>
          <li>
            Candidate channels are demoted by an FCS-error penalty when a same-block neighbour
            reports fcs_error_rate &gt; 5% — catches non-WiFi interference (microwave, radar).
          </li>
        </UL>
        Pair severity also scaled by worst-of-two mean child uplink MCS (low-MCS = more airtime per
        byte). After each channel pass, the next Topology iteration sees the updated RF environment.
        <br /><br />
        <strong>Channel groups (fallback safety net):</strong> every channel action emits a{' '}
        <strong>channel_groups</strong> payload containing the primary pick plus up to 2 alternates.
        The device&apos;s on-board ACS layer can fall back to an alternate if the primary becomes
        noisy after the plan applies — no need to wait for the next Optimize run. The full group
        list is shown in the Channels panel under each action.
        <br /><br />
        <strong>Cross-run oscillation guard:</strong> when the persistent RRM state cache shows a
        radio flapping A→B→A→B across recent plans, the optimizer applies a 5× stricter
        improvement floor and surfaces a <em>channel_oscillation</em> advisory issue. The next
        plan effectively requires a much bigger improvement before moving the radio at all.
        <br /><br />
        <strong>2.4 GHz is never touched.</strong> 2.4 GHz radios, surveys, and neighbour
        observations are filtered out at ingest — the optimizer plans only 5/6 GHz tree-management
        radios.
      </>
    ),
    algorithm: 'DSATUR graph colouring; severity = (spectral_overlap² · 0.7 + distance_proximity · 0.3) × load_factor(foreign_airtime or busy) × mcs_factor(mean_child_mcs); spectral overlap uses real bonded-block intervals at each AP\'s effective BW. BW-aware: filters channels by max bandwidth, hard-rejects same-block critical neighbours, second pass at lower BW if high-severity conflicts persist. Emits channel_groups payload (primary + 2 alternates) so device ACS can fall back. DFS penalty 0.4 for Nodes under default regulatory. Cross-run oscillation detector boosts the floor 5× on flapping radios.',
    measures:
      'Sum of weighted channel conflicts — lower is better. Load-weighted by foreign airtime (or legacy busy fraction) AND mean child uplink MCS.',
    actsOn: 'POST /tree-mgmt/{serial}/set-ap-channel with channel_groups payload — sets per-radio channel-list (primary + alternates) and optionally htmode for bandwidth downgrades.',
  },

  [ALGO_BACKUP]: {
    id: ALGO_BACKUP,
    title: 'Backup Parents',
    short: "Failover advisory (Yen's k-shortest paths)",
    icon: SettingsBackupRestoreIcon,
    color: 'primary',
    isMeta: false,
    advisory: true,
    tooltip:
      "Yen's k-shortest paths per Node. Edge weights use EffectiveLinkCostV2 (phy_rate_mbps + recent deltas when rf_telemetry available). Advisory only — no actions emitted.",
    description: (
      <>
        Computes Yen&apos;s k-shortest paths to a CAP for every Node and lists the top-k
        alternatives.
        <UL>
          <li>
            Edge weights use <strong>EffectiveLinkCostV2</strong> — incorporates phy_rate_mbps and
            recent per-window loss/retry deltas when rf_telemetry is available.
          </li>
          <li>
            Pure advisory — no actions emitted. Use to answer: does every Node have at least one
            decent backup path?
          </li>
        </UL>
      </>
    ),
    algorithm: "Yen's k-shortest paths over the parent graph; edge weights from EffectiveLinkCostV2 (telemetry-fused when available)",
    measures: 'Percentage of Nodes that have at least one backup path — higher is better.',
    actsOn: 'Nothing — advisory only.',
  },
};
